<template>
  <button type="button" class="tour-btn" :class="{ 'tour-btn--compact': compact }" :aria-label="label">
    <span class="tour-btn__icon" aria-hidden="true">
      <i class="fas fa-circle-question"></i>
      <i class="fas fa-play tour-btn__play"></i>
    </span>
    <span v-if="!compact" class="tour-btn__label">{{ label }}</span>
  </button>
</template>

<script>
// The one way into a guided walkthrough, everywhere the app offers one
// (Add File, Run Calculator, Run nMonte). One mark for all three: the
// question-circle says "help", the small play badge on its corner says
// "this one walks you through it". Two-tone blue, like the starter-model
// Load button, so it sits with the app's blue actions without shouting.
//
// Positioning is the caller's business: give the button an id and keep
// the page's placement rules on that id. Visual rules live here.
export default {
  name: 'TourButton',
  props: {
    label: { type: String, default: 'Walkthrough' },
    // Icon only, for tight spots (mobile strip).
    compact: { type: Boolean, default: false },
  },
}
</script>

<style lang="scss">
// Two-tone blue like the starter-model Load button: a translucent
// fill inside a solid border, filling in on hover. Light first,
// body.dark swaps the tokens.
.tour-btn {
  --tb-line: #2e668c;
  --tb-fill: rgba(46, 102, 140, 0.1);
  --tb-text: #2e668c;
  --tb-fill-hover: #2e668c;
  --tb-text-hover: #ffffff;
  --tb-ring: #ffffff;

  appearance: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 28px;
  margin: 0;
  padding: 0 12px 0 9px;
  border: 1px solid var(--tb-line);
  border-radius: 20px;
  background: var(--tb-fill);
  color: var(--tb-text);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s, transform 0.15s;

  &:hover {
    background: var(--tb-fill-hover);
    border-color: var(--tb-fill-hover);
    color: var(--tb-text-hover);
  }
  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: 2px solid var(--tb-line);
    outline-offset: 2px;
  }

  &__icon {
    position: relative;
    display: inline-flex;
    width: 16px;
    height: 16px;
    font-size: 16px;
    line-height: 1;
    > i { line-height: 1; }
  }

  // The play badge on the question mark's bottom-right corner: a solid
  // blue dot with a ring in the page ground so it reads over the glyph.
  &__play {
    position: absolute;
    right: -5px;
    bottom: -4px;
    box-sizing: border-box;
    width: 11px;
    height: 11px;
    padding-left: 1px;
    border-radius: 50%;
    border: 1.5px solid var(--tb-ring);
    background: var(--tb-line);
    color: #fff;
    font-size: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  &:hover &__play {
    border-color: var(--tb-fill-hover);
    background: var(--tb-ring);
    color: var(--tb-fill-hover);
  }

  &--compact {
    width: 30px;
    height: 30px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

body.dark .tour-btn {
  --tb-line: #5ba3c9;
  --tb-fill: rgba(91, 163, 201, 0.17);
  --tb-text: #ffffff;
  --tb-fill-hover: #7dc0df;
  --tb-text-hover: #ffffff;
  --tb-ring: #212121;
}
</style>

<style lang="scss">
// A walkthrough button and its video button, side by side. The wrapper takes
// whatever positioning the page used to put on the tour button alone
// (#g25helpcluster, #admixhelpcluster, #samplehelpcluster), so the pair moves
// as one and the ids intro.js targets stay on the buttons.
.help-cluster {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
